import { VideoSidebar } from "common/src/gamePage/types";
import { _any, _ } from "i18n";
import { ChevronLeft } from "lucide-react";
import { useGameStore } from "../../store";
import { useMemo, useState } from "react";
import { TeamSheet } from "./TeamSheet";
import { Filter } from "./Filter/Filter";
import { Timeline } from "./Timeline/Timeline";
import { EventList } from "./EventList/EventList";
import type {
	GPGameTeamResponse,
	GameClipIntervalRequest,
	GamePageResponse,
	GamePageLazyResponse,
	HighlightHandlers,
	GameEventDataExportRequest,
	GameEventFilterUtilityProps,
	GameEventCommentProps,
	RatingPutRequest,
	GPGamePlayerResponse,
	PlayerGradingSubmitPayload,
	CoachReviewPromises,
} from "../../types";
import type { ReportEventErrorHandler } from "../../report-error";
import { CreateInterface } from "./Create/CreateInterface";
import { DownloadTeamSheet } from "./DownloadTeamSheet";
import { DownloadEvents } from "./EventList/DownloadEvents";
import { CoachingToolsList } from "./CoachingTools/List";
import { AttachModeSidebar } from "./AttachModeSidebar";
import { AttachModeSidebarView, useAttachStore } from "../../attach-store";
import { CoachReviewsContainer } from "./CoachReviews/Container";
import { CoachReviewsProvider } from "common/src/gamePage/Contexts/coach-reviews.context";

interface SidebarContentProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	highlightsHandlers: HighlightHandlers;
	handleCreateClip: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	handleOpenEditTeamSheetModal: (team: GPGameTeamResponse) => void;
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void;
	gameEventFilters?: GameEventFilterUtilityProps;
	handleTeamSheetDataExport: (type: string) => void;
	sidebar: VideoSidebar | null;
	setSidebar: (sidebar: VideoSidebar | null) => void;
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
	gameEventComment?: GameEventCommentProps;
	players?: GPGamePlayerResponse[];
	onReportError?: ReportEventErrorHandler;
	coachReviewPromises?: CoachReviewPromises;
	userId?: number;
	activeContextTeamId?: number;
}

export function SidebarContent({
	gameData,
	gameDataLazy,
	handleOpenEditTeamSheetModal,
	highlightsHandlers,
	handleCreateClip,
	handleGameEventDataExport,
	gameEventFilters,
	handleTeamSheetDataExport,
	sidebar,
	setSidebar,
	handleSetPlayerRating,
	handleSubmitPlayerGrading,
	toastMessage,
	gameEventComment,
	players,
	onReportError,
	coachReviewPromises,
	userId,
	activeContextTeamId,
}: SidebarContentProps) {
	const { sidebarCommentEventId, setSidebarCommentEventId } = useGameStore();
	const { target: attachTarget, sidebarView: attachSidebarView, setSidebarView: setAttachSidebarView } = useAttachStore();
	const isEventCommentMode = sidebarCommentEventId != null && (sidebar === VideoSidebar.EventList || sidebar === VideoSidebar.EventsWithComments);
	const isCoachingToolsReviewMode = sidebar === VideoSidebar.TeamPlayerReviews || sidebar === VideoSidebar.AttachMode;
	const activeContextGameTeam = [gameData.homeGameTeam, gameData.awayGameTeam].find(
		(gameTeam) => activeContextTeamId != null && gameTeam.team?.id === activeContextTeamId
	);
	const clipsPermission = gameData.permissions.useClips;
	const eventsPermission = gameData.permissions.showEvents;
	const reportEventErrorPermission = gameData.permissions.reportEventError;
	const eventCommentsPermission = gameData.permissions.showEventComments;
	const teamInfo = useMemo(
		() => ({
			homeTeam: {
				id: gameData.homeGameTeam.id,
				shirtColor: gameData.homeGameTeam.colorShirt,
				clubLogo: gameData.homeGameTeam.logo,
				clubName: gameData.homeGameTeam.clubName ?? gameData.homeGameTeam.teamName,
			},
			awayTeam: {
				id: gameData.awayGameTeam.id,
				shirtColor: gameData.awayGameTeam.colorShirt,
				clubLogo: gameData.awayGameTeam.logo,
				clubName: gameData.awayGameTeam.clubName ?? gameData.awayGameTeam.teamName,
			},
		}),
		[
			gameData.homeGameTeam.id,
			gameData.homeGameTeam.colorShirt,
			gameData.homeGameTeam.logo,
			gameData.homeGameTeam.clubName,
			gameData.homeGameTeam.teamName,
			gameData.awayGameTeam.id,
			gameData.awayGameTeam.colorShirt,
			gameData.awayGameTeam.logo,
			gameData.awayGameTeam.clubName,
			gameData.awayGameTeam.teamName,
		]
	);
	const canWriteReviews = gameData.permissions.showAiCoachReview;
	const canAttachReviews = gameData.homeGameTeam.permissions.reviewEdit || gameData.awayGameTeam.permissions.reviewEdit;

	const handleHeaderBackClick = () => {
		if (sidebar === VideoSidebar.AttachMode && attachSidebarView === AttachModeSidebarView.Filters) {
			setAttachSidebarView(AttachModeSidebarView.EventList);
			return;
		}

		if (sidebar === VideoSidebar.Filters) {
			setSidebar(VideoSidebar.EventList);
			return;
		}

		if (sidebar === VideoSidebar.AttachMode) {
			setSidebar(VideoSidebar.TeamPlayerReviews);
			return;
		}

		if (sidebar === VideoSidebar.TeamPlayerReviews) {
			setSidebar(VideoSidebar.CoachingTools);
			return;
		}

		if (isEventCommentMode) {
			setSidebarCommentEventId(null);
		}
	};

	const shouldShowHeaderBackButton =
		isEventCommentMode ||
		sidebar === VideoSidebar.TeamPlayerReviews ||
		sidebar === VideoSidebar.Filters ||
		(sidebar === VideoSidebar.AttachMode && attachSidebarView === AttachModeSidebarView.Filters);

	const getHeaderTitle = () => {
		if (isEventCommentMode) {
			return _any("discover.breadcrumb.back");
		}

		if (sidebar === VideoSidebar.AttachMode) {
			return attachSidebarView === AttachModeSidebarView.Filters ? _("gamePage.label.filters") : _("gamePage.label.eventList");
		}

		if (isCoachingToolsReviewMode) {
			return _("gamePage.label.coachingTools");
		}

		if (sidebar) {
			return _any(`gamePage.label.${sidebar}`);
		}

		return "";
	};

	return (
		<>
			{sidebar && (
				<div className="sidebarContent-container">
					<div className="sidebarContent-header">
						<div className="headerText">
							{shouldShowHeaderBackButton && (
								<button
									type="button"
									className="sidebarContent-header-back"
									onClick={handleHeaderBackClick}
									aria-label={_("btn.cancel")}
								>
									<ChevronLeft className="w-5 h-5" strokeWidth={2} />
								</button>
							)}
							<span className="sidebarContent-header-title">{getHeaderTitle()}</span>
						</div>
						<div className="header-controls">
							{sidebar === VideoSidebar.TeamSheet && (
								<DownloadTeamSheet
									types={gameData.game.teamSheetDownloadTypes}
									handleTeamSheetDataExport={handleTeamSheetDataExport}
								/>
							)}
							{(sidebar === VideoSidebar.EventList || sidebar === VideoSidebar.EventsWithComments) &&
								gameData.permissions.downloadEvents &&
								!isEventCommentMode && (
									<DownloadEvents
										handleGameEventDataExport={handleGameEventDataExport}
										allowedPlayers={gameData.permissions.allowedPlayers}
									/>
								)}
							<span className="icon-new icon-light-cross icon-14" onClick={() => setSidebar(null)} />
						</div>
					</div>
					{sidebar === VideoSidebar.TeamSheet && (
						<TeamSheet
							gameData={gameData}
							handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
							handleSetPlayerRating={handleSetPlayerRating}
							handleSubmitPlayerGrading={handleSubmitPlayerGrading}
							toastMessage={toastMessage}
						/>
					)}
					{sidebar === VideoSidebar.Filters && (
						<Filter gameData={gameData} gameDataLazy={gameDataLazy} gameEventFilters={gameEventFilters} />
					)}
					{sidebar === VideoSidebar.Timeline && <Timeline gameData={gameData} gameDataLazy={gameDataLazy} />}
					{(sidebar === VideoSidebar.EventList || sidebar === VideoSidebar.EventsWithComments) && (
						<EventList
							teamInfo={teamInfo}
							clipsPermission={clipsPermission}
							eventsPermission={eventsPermission}
							reportEventErrorPermission={reportEventErrorPermission}
							onReportError={onReportError}
							handleCreateClip={handleCreateClip}
							eventCommentsPermission={eventCommentsPermission}
							gameEventComment={gameEventComment}
							players={players}
						/>
					)}
					{sidebar === VideoSidebar.EditEvent && (
						<CreateInterface onEventSubmit={highlightsHandlers.edit} onDelete={highlightsHandlers.delete} type="event" edit />
					)}
					{sidebar === VideoSidebar.CreateEvent && <CreateInterface onEventSubmit={highlightsHandlers.create} type="event" />}
					{sidebar === VideoSidebar.CreateClip && <CreateInterface onClipSubmit={handleCreateClip} type="clip" />}
					{(sidebar === VideoSidebar.CoachingTools || sidebar === VideoSidebar.TeamPlayerReviews || sidebar === VideoSidebar.AttachMode) &&
					activeContextGameTeam !== undefined &&
					userId !== undefined &&
					coachReviewPromises !== undefined ? (
						<CoachReviewsProvider
							userId={userId}
							gameId={gameData.game.id}
							teamId={activeContextGameTeam.team?.id ?? null}
							gameTeamId={activeContextGameTeam.id}
							promises={coachReviewPromises}
						>
							{sidebar === VideoSidebar.CoachingTools && <CoachingToolsList />}
							{sidebar === VideoSidebar.TeamPlayerReviews && canWriteReviews && canAttachReviews && <CoachReviewsContainer />}
							{sidebar === VideoSidebar.AttachMode && attachTarget !== null && (
								<AttachModeSidebar
									target={attachTarget}
									gameData={gameData}
									gameDataLazy={gameDataLazy}
									gameEventFilters={gameEventFilters}
									teamInfo={teamInfo}
									clipsPermission={clipsPermission}
									eventsPermission={eventsPermission}
									reportEventErrorPermission={reportEventErrorPermission}
									onReportError={onReportError}
									handleCreateClip={handleCreateClip}
									eventCommentsPermission={eventCommentsPermission}
									gameEventComment={gameEventComment}
									players={players}
								/>
							)}
						</CoachReviewsProvider>
					) : null}
				</div>
			)}
		</>
	);
}
